import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { PRICING } from '../constants/pricing';

const BLUE       = 'var(--signal)';
const TEXT1      = 'var(--ink)';
const TEXT2      = 'var(--slate)';
const TEXT3      = 'var(--mute)';
const GREEN      = 'var(--go)';
const GREEN_BG   = 'var(--go-soft)';
const GREEN_BORDER = 'var(--go-border)';

// Savings math for the annual card, derived from PRICING so the numbers can
// never drift from the prices shown above them.
const ANNUAL_SAVINGS = PRICING.monthly.intro * 12 - PRICING.annual.intro;
const ANNUAL_PER_MONTH = (PRICING.annual.intro / 12).toFixed(2);

// Outcomes, not feature names — the reserve calculator itself was already
// free during onboarding, so it can't lead.
const MONTHLY_FEATURES = [
  'Your number stays right as the year changes',
  'Slow months show up before they arrive',
  'IRS payments walked through step by step',
];

const ANNUAL_FEATURES = [
  `Save $${ANNUAL_SAVINGS} a year — works out to $${ANNUAL_PER_MONTH} a month`,
  'Your number stays right as the year changes',
  'Slow months show up before they arrive',
  'IRS payments walked through step by step',
];

// Coarse on purpose: "about" paired with a to-the-dollar figure reads as
// false precision.
function fmtApprox(n) {
  return '$' + (Math.floor(n / 50) * 50).toLocaleString();
}

// Same sticky-header markup the other back-navigable screens use (Settings,
// AddExpense, EditExpense, TaxEdit, CurveEdit, IRSPayment). No shared component
// exists — each screen carries its own copy — so this mirrors theirs exactly.
// Not a tab, so no BottomTabBar: the arrow is the whole affordance.
function StickyHeader({ onBack }) {
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 100,
      height: 56, backgroundColor: 'var(--card)',
      borderBottom: '0.5px solid rgba(0,0,0,0.12)',
      display: 'flex', alignItems: 'center',
      padding: '0 20px', gap: 10,
    }}>
      <span
        onClick={onBack}
        style={{ fontSize: 18, color: TEXT2, cursor: 'pointer', lineHeight: 1, paddingRight: 4, userSelect: 'none' }}
      >
        ←
      </span>
      <span style={{ fontFamily: 'var(--font-serif)', fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.3px' }}>
        <span style={{ color: TEXT1 }}>Tru</span>
        <span style={{ color: BLUE }}>Flow</span>
      </span>
    </div>
  );
}

function CheckMark() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
      <circle cx="7" cy="7" r="7" style={{ fill: 'var(--signal-soft)' }} />
      <polyline points="4,7 6,9 10,5" style={{ stroke: BLUE }} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

function PlanCard({ badge, badgeColor, title, price, period, description, features, buttonLabel, priceId, highlight }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleClick() {
    if (!token) {
      navigate('/login');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/payments/create-checkout-session', { price_id: priceId });
      window.location.href = res.data.checkout_url;
    } catch {
      setError('Could not start checkout. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div style={{
      backgroundColor: 'var(--card)',
      border: highlight ? `2px solid ${BLUE}` : 'none',
      boxShadow: 'var(--sh-card)',
      borderRadius: 'var(--r-card)',
      padding: 24,
      position: 'relative',
      boxSizing: 'border-box',
    }}>
      {badge && (
        <div style={{
          display: 'inline-block',
          backgroundColor: badgeColor === 'green' ? GREEN_BG : 'var(--signal-soft)',
          border: `1px solid ${badgeColor === 'green' ? GREEN_BORDER : 'var(--signal-border)'}`,
          color: badgeColor === 'green' ? GREEN : BLUE,
          fontSize: 10,
          fontWeight: 600,
          borderRadius: 20,
          padding: '3px 10px',
          marginBottom: 12,
          letterSpacing: '0.2px',
        }}>
          {badge}
        </div>
      )}

      <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: TEXT1 }}>
        {title}
      </p>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, margin: '8px 0 4px' }}>
        <span style={{ fontFamily: 'var(--font-serif)', fontSize: 32, fontWeight: 700, color: TEXT1 }}>{price}</span>
        <span style={{ fontSize: 13, color: TEXT3 }}>/{period}</span>
      </div>

      <p style={{ margin: '0 0 16px', fontSize: 12, color: TEXT2, lineHeight: 1.5 }}>
        {description}
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
        {features.map((f) => (
          <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <CheckMark />
            <span style={{ fontSize: 13, color: TEXT2, lineHeight: 1.4 }}>{f}</span>
          </div>
        ))}
      </div>

      {error && (
        <p style={{
          color: 'var(--stop)', fontSize: 12, margin: '0 0 12px',
          padding: '8px 10px', backgroundColor: 'var(--stop-soft)',
          border: '1px solid var(--stop-border)', borderRadius: 8,
        }}>
          {error}
        </p>
      )}

      <button
        onClick={handleClick}
        disabled={loading}
        style={{
          width: '100%',
          backgroundColor: loading ? 'var(--signal-disabled)' : BLUE,
          color: 'var(--on-signal)',
          border: 'none',
          borderRadius: 10,
          padding: '12px 0',
          fontSize: 14,
          fontWeight: 600,
          cursor: loading ? 'default' : 'pointer',
        }}
      >
        {loading ? 'Redirecting…' : buttonLabel}
      </button>
    </div>
  );
}

export default function Pricing() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [reserve, setReserve] = useState(null);

  // React Router stamps an index onto history.state; 0 means this entry is the
  // first in the session, so there is nothing to go back to (direct URL, fresh
  // tab, or an external link). Fall back into the app instead of a dead arrow.
  function handleBack() {
    const canGoBack = (window.history.state?.idx ?? 0) > 0;
    if (canGoBack) navigate(-1);
    else navigate(token ? '/dashboard' : '/');
  }

  // Personalization only — the page renders complete without it, so this never
  // blocks paint and never shows a spinner. No token means no request at all.
  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    async function loadReserve() {
      try {
        const res = await api.get('/tax/calculate');
        if (cancelled) return;
        const value = res.data?.recommended_reserve;
        // Below the rounding step the approximate figure would render as $0,
        // so anything that small keeps the generic line.
        if (typeof value === 'number' && value >= 50) setReserve(value);
      } catch {
        // 404 (no tax setup yet), 402, or a network blip — the generic line
        // below already reads as finished copy, so there is nothing to do.
      }
    }
    loadReserve();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div style={{ backgroundColor: 'var(--paper)', minHeight: '100vh', boxSizing: 'border-box' }}>
      <StickyHeader onBack={handleBack} />
      <div style={{ maxWidth: 480, margin: '0 auto', padding: '40px 16px 60px' }}>
        <h1 style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 26,
          fontWeight: 700,
          color: TEXT1,
          margin: '0 0 8px',
          textAlign: 'center',
        }}>
          Your plan is ready — pick how you want to keep it
        </h1>
        <p style={{
          fontSize: 13,
          color: TEXT2,
          textAlign: 'center',
          margin: '0 0 32px',
          lineHeight: 1.6,
        }}>
          Cancel anytime — your setup is saved. Intro rate for your first year, then standard pricing.
        </p>

        {/* Fixed height so swapping in the personalized line when the fetch
            lands never pushes the plan cards down the page. */}
        <div style={{ minHeight: 42, marginBottom: 8 }}>
          <p style={{
            fontSize: 13,
            fontWeight: 500,
            color: TEXT1,
            textAlign: 'center',
            margin: 0,
            lineHeight: 1.6,
          }}>
            {reserve
              ? `You're setting aside about ${fmtApprox(reserve)} every quarter. TruFlow is $${PRICING.monthly.intro} a month.`
              : `Set aside the right amount every quarter, without guessing. TruFlow is $${PRICING.monthly.intro} a month.`}
          </p>
        </div>

        <p style={{
          fontSize: 13,
          color: TEXT1,
          textAlign: 'center',
          margin: '0 0 24px',
          lineHeight: 1.6,
        }}>
          Underpay a quarter and the IRS charges interest on the difference until it's settled.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <PlanCard
            badge="Intro rate"
            badgeColor="blue"
            title="Monthly"
            price={`$${PRICING.monthly.intro}`}
            period="month"
            description={`$${PRICING.monthly.intro}/mo for your first ${PRICING.monthly.introMonths} months, then $${PRICING.monthly.base}/mo`}
            features={MONTHLY_FEATURES}
            buttonLabel="Get started"
            priceId={process.env.REACT_APP_STRIPE_PRICE_MONTHLY}
            highlight
          />
          <PlanCard
            badge="Best Value"
            badgeColor="green"
            title="Annual"
            price={`$${PRICING.annual.intro}`}
            period="year"
            description={`$${PRICING.annual.intro}/yr for your first year, then $${PRICING.annual.base}/yr`}
            features={ANNUAL_FEATURES}
            buttonLabel="Get started"
            priceId={process.env.REACT_APP_STRIPE_PRICE_ANNUAL}
          />
        </div>

        <p style={{
          marginTop: 32,
          fontSize: 11,
          color: TEXT3,
          textAlign: 'center',
          lineHeight: 1.6,
        }}>
          TruFlow is not a tax advisor. Consult a CPA for personalized advice.
        </p>
      </div>
    </div>
  );
}
